import toast from 'react-hot-toast';
import { QueryClient, useMutation } from 'react-query';

import {
  FollowAction,
  FollowType,
  UpdateFollowArgs,
  updateFollowEntity,
  updateFollowIdentity,
} from '../utils/backend-api/follow';
import { generateSubscriberKey } from '../utils/backend-api/subscriber_directory';
import { generateFollowedEntitiesKey } from './useFollowedEntities';

interface UseFollowUpdateArgs extends UpdateFollowArgs {
  followType: FollowType;
  // depending on page will either have follower's identityid or username
  identityId?: number | string;
  username?: string;
  queryClient: QueryClient;
}

const READABLE_FOLLOW_TYPE = {
  [FollowType.Entity]: 'topic',
  [FollowType.Identity]: 'user',
};

const trackAhoy = (
  followAction: FollowAction,
  FollowType: FollowType,
  followeeId: number
): void => {
  // @ts-expect-error TODO: import ahoy instead of using global instance?
  ahoy.track(`${followAction}-click`, {
    item_type: FollowType,
    item_id: followeeId,
    // @ts-expect-error TODO: use currentUser from context instead of global var
    user_id: currentUser.id,
    page: window.location.pathname,
    url: window.location.href,
  });
};

export default function useFollowUpdate() {
  const { mutate, mutateAsync, data, isLoading, isError } = useMutation(
    (args: UseFollowUpdateArgs) =>
      args.followType === FollowType.Identity
        ? updateFollowIdentity({ followeeId: args.followeeId, followAction: args.followAction })
        : updateFollowEntity({ followeeId: args.followeeId, followAction: args.followAction }),
    {
      onSuccess: (
        _,
        { followeeId, followAction, followType, identityId, username, queryClient }
      ) => {
        if (followType === FollowType.Identity && username && queryClient) {
          queryClient.invalidateQueries(generateSubscriberKey(username));
        } else if (followType === FollowType.Entity) {
          queryClient.invalidateQueries(generateFollowedEntitiesKey(identityId));
        }

        trackAhoy(followAction, followType, followeeId);
      },
      onError: (_, { followType, followAction }) => {
        const FOLLOW_ERROR_MESSAGE = `There was an issue ${followAction}ing this ${READABLE_FOLLOW_TYPE[followType]}, please try again later`;
        toast.error(FOLLOW_ERROR_MESSAGE);
      },
    }
  );
  return { mutate, mutateAsync, data, isLoading, isError };
}
